// Demo datasets — Stage 10 (PSD §10.3, "Load Demo" pattern).
//
// Each dataset is a complete, valid ValuationInputs snapshot (cap table +
// assumptions + equity-value anchor + discounts + compliance + solver cfg).
// The store's `loadDemoDataset` action wraps these into a ValuationRecord,
// sets it active, and runs the engine so the UI is in a meaningful working
// state — the primary onboarding path per the pipeline spec.
//
// ── Dataset count justification ───────────────────────────────────────────
// The engine (src/engine/runValuation.ts) dispatches on two orthogonal axes:
//   • allocation method: opm | pwer | hybrid | cvm
//   • equity-value source: backsolve | dcf | marketMultiples | tender
//
// Five datasets cover the meaningful result-mode space without redundancy:
//   1. seed-stage-clean       — minimal/simple OPM backsolve (1 common + 1 pref)
//   2. series-a-with-pool     — typical mid-complexity OPM backsolve (+ option pool)
//   3. late-stage-multi-class — maximal/stress OPM (3 seniority tiers + RSUs +
//                               warrants, direct equity value via tender)
//   4. pwerm-scenario         — distinct branch: probability-weighted outcomes
//   5. hybrid-blend           — distinct branch: OPM/PWERM blend
//
// CVM is intentionally omitted: it is a degenerate per-share = V / FD split
// with no breakpoints, no allocation, and no solver — already covered by the
// engine's own unit tests and adds no integration-test value over the OPM
// direct case (#3) which exercises the same `directEquityValue > 0` code path.
// ── Dataset 1 — minimal/simple OPM backsolve ──────────────────────────────
const seedStageClean = {
    id: 'demo_seed',
    name: 'Seed-Stage Clean Cap Table',
    description: 'Minimal OPM backsolve: one common class + one Series Seed preferred. The simplest convergent case.',
    inputs: {
        valuationId: 'demo_seed',
        capTableSnapshot: {
            id: 'cap_demo_seed',
            version: 1,
            asOfDate: '2026-06-30',
            entityName: 'Seedstack, Inc.',
            currency: 'USD',
            instruments: [
                {
                    id: 'common',
                    name: 'Common Stock',
                    kind: 'common',
                    sharesOutstanding: 4_000_000,
                    sharesAuthorized: 4_000_000,
                    issuePrice: 0.0001,
                    issueDate: '2023-01-01',
                    liquidationMultiple: null,
                    participation: null,
                    participationCap: null,
                    seniority: null,
                    conversionRatio: null,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'seed',
                    name: 'Series Seed Preferred',
                    kind: 'preferred',
                    sharesOutstanding: 1_000_000,
                    sharesAuthorized: 1_000_000,
                    issuePrice: 1.0,
                    issueDate: '2024-03-15',
                    liquidationMultiple: 1.0,
                    participation: 'nonParticipating',
                    participationCap: null,
                    seniority: 1,
                    conversionRatio: 1.0,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
            ],
            convertibles: [],
        },
        assumptions: {
            volatility: 0.6,
            timeToLiquidity: 5.0,
            riskFreeRate: 0.0425,
            dividendYield: 0,
            method: 'opm',
            methodRationale: 'Simple OPM backsolve off the recent Seed round.',
        },
        equityValue: {
            source: 'backsolve',
            anchorInstrumentId: 'seed',
            anchorPricePerShare: 1.0,
            directEquityValue: null,
            armLengthFlag: true,
            transactionDate: '2026-05-15',
        },
        discounts: { dlomModel: 'fixed', dlomPercentage: 0.25, minorityDiscount: 0 },
        compliance: {
            valuationDate: '2026-07-01',
            grantDate: null,
            safeHarbor: 'illiquidStartup',
        },
        solverTolerance: 0.005,
        solverMaxIterations: 100,
    },
};
// ── Dataset 2 — typical mid-complexity OPM backsolve ──────────────────────
const seriesAWithPool = {
    id: 'demo_series_a',
    name: 'Series A with Option Pool',
    description: 'Typical mid-complexity OPM backsolve: common, Series A preferred, and a 10% authorized option pool.',
    inputs: {
        valuationId: 'demo_series_a',
        capTableSnapshot: {
            id: 'cap_demo_series_a',
            version: 1,
            asOfDate: '2026-06-30',
            entityName: 'Aerio Labs, Inc.',
            currency: 'USD',
            instruments: [
                {
                    id: 'common',
                    name: 'Common Stock',
                    kind: 'common',
                    sharesOutstanding: 10_000_000,
                    sharesAuthorized: 12_000_000,
                    issuePrice: 0.0001,
                    issueDate: '2021-01-01',
                    liquidationMultiple: null,
                    participation: null,
                    participationCap: null,
                    seniority: null,
                    conversionRatio: null,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'prefA',
                    name: 'Series A Preferred',
                    kind: 'preferred',
                    sharesOutstanding: 5_000_000,
                    sharesAuthorized: 5_000_000,
                    issuePrice: 2.0,
                    issueDate: '2024-09-01',
                    liquidationMultiple: 1.0,
                    participation: 'nonParticipating',
                    participationCap: null,
                    seniority: 1,
                    conversionRatio: 1.0,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'pool',
                    name: 'Option Pool',
                    kind: 'pool',
                    sharesOutstanding: 0,
                    sharesAuthorized: 1_500_000,
                    issuePrice: 0.0001,
                    issueDate: null,
                    liquidationMultiple: null,
                    participation: null,
                    participationCap: null,
                    seniority: null,
                    conversionRatio: null,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
            ],
            convertibles: [],
        },
        assumptions: {
            volatility: 0.55,
            timeToLiquidity: 5.0,
            riskFreeRate: 0.0425,
            dividendYield: 0,
            method: 'opm',
            methodRationale: 'Standard OPM backsolve off the Series A anchor.',
        },
        equityValue: {
            source: 'backsolve',
            anchorInstrumentId: 'prefA',
            anchorPricePerShare: 2.0,
            directEquityValue: null,
            armLengthFlag: true,
            transactionDate: '2026-04-20',
        },
        discounts: { dlomModel: 'fixed', dlomPercentage: 0.25, minorityDiscount: 0 },
        compliance: {
            valuationDate: '2026-07-01',
            grantDate: null,
            safeHarbor: 'illiquidStartup',
        },
        solverTolerance: 0.005,
        solverMaxIterations: 100,
    },
};
// ── Dataset 3 — maximal/stress OPM with direct equity value ───────────────
const lateStageMultiClass = {
    id: 'demo_late_stage',
    name: 'Late-Stage Multi-Class',
    description: 'Stress case: 3 seniority tiers of preferred, RSUs, and an outstanding warrant, valued off a tender-offer direct equity value.',
    inputs: {
        valuationId: 'demo_late_stage',
        capTableSnapshot: {
            id: 'cap_demo_late_stage',
            version: 1,
            asOfDate: '2026-06-30',
            entityName: 'Northbound Holdings, Inc.',
            currency: 'USD',
            instruments: [
                {
                    id: 'common',
                    name: 'Common Stock',
                    kind: 'common',
                    sharesOutstanding: 10_000_000,
                    sharesAuthorized: 10_000_000,
                    issuePrice: 0.0001,
                    issueDate: '2018-01-01',
                    liquidationMultiple: null,
                    participation: null,
                    participationCap: null,
                    seniority: null,
                    conversionRatio: null,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'prefA',
                    name: 'Series A Preferred',
                    kind: 'preferred',
                    sharesOutstanding: 3_000_000,
                    sharesAuthorized: 3_000_000,
                    issuePrice: 1.0,
                    issueDate: '2019-06-01',
                    liquidationMultiple: 1.0,
                    participation: 'nonParticipating',
                    participationCap: null,
                    seniority: 3,
                    conversionRatio: 1.0,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'prefB',
                    name: 'Series B Preferred',
                    kind: 'preferred',
                    sharesOutstanding: 2_500_000,
                    sharesAuthorized: 2_500_000,
                    issuePrice: 2.5,
                    issueDate: '2021-09-01',
                    liquidationMultiple: 1.0,
                    participation: 'nonParticipating',
                    participationCap: null,
                    seniority: 2,
                    conversionRatio: 1.0,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'prefC',
                    name: 'Series C Preferred',
                    kind: 'preferred',
                    sharesOutstanding: 2_000_000,
                    sharesAuthorized: 2_000_000,
                    issuePrice: 4.5,
                    issueDate: '2023-11-15',
                    liquidationMultiple: 1.0,
                    participation: 'nonParticipating',
                    participationCap: null,
                    seniority: 1,
                    conversionRatio: 1.0,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'rsu',
                    name: 'Employee RSUs',
                    kind: 'rsu',
                    sharesOutstanding: 400_000,
                    sharesAuthorized: 400_000,
                    issuePrice: 0,
                    issueDate: null,
                    liquidationMultiple: null,
                    participation: null,
                    participationCap: null,
                    seniority: null,
                    conversionRatio: null,
                    strikePrice: null,
                    vestingMonths: 48,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'warrant',
                    name: 'Strategic Warrant',
                    kind: 'warrant',
                    sharesOutstanding: 250_000,
                    sharesAuthorized: 250_000,
                    issuePrice: 0,
                    issueDate: '2022-02-01',
                    liquidationMultiple: null,
                    participation: null,
                    participationCap: null,
                    seniority: null,
                    conversionRatio: null,
                    strikePrice: 1.5,
                    vestingMonths: null,
                    expirationDate: '2029-12-31',
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'pool',
                    name: 'Option Pool',
                    kind: 'pool',
                    sharesOutstanding: 0,
                    sharesAuthorized: 2_000_000,
                    issuePrice: 0.0001,
                    issueDate: null,
                    liquidationMultiple: null,
                    participation: null,
                    participationCap: null,
                    seniority: null,
                    conversionRatio: null,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
            ],
            convertibles: [],
        },
        assumptions: {
            volatility: 0.45,
            timeToLiquidity: 4.0,
            riskFreeRate: 0.0425,
            dividendYield: 0,
            method: 'opm',
            methodRationale: 'Tender-anchored OPM; multi-class waterfall with deep seniority.',
        },
        equityValue: {
            source: 'tender',
            anchorInstrumentId: null,
            anchorPricePerShare: null,
            directEquityValue: 80_000_000,
            armLengthFlag: true,
            transactionDate: '2026-05-01',
        },
        discounts: { dlomModel: 'fixed', dlomPercentage: 0.2, minorityDiscount: 0 },
        compliance: {
            valuationDate: '2026-07-01',
            grantDate: null,
            safeHarbor: 'independentAppraisal',
        },
        solverTolerance: 0.005,
        solverMaxIterations: 100,
    },
};
// ── Dataset 4 — PWERM branch ──────────────────────────────────────────────
const pwermScenario = {
    id: 'demo_pwerm',
    name: 'PWERM Scenario Set',
    description: 'Probability-weighted expected return method: IPO / acquisition / down-round / failure outcomes, anchored to a direct equity value.',
    inputs: {
        valuationId: 'demo_pwerm',
        capTableSnapshot: {
            id: 'cap_demo_pwerm',
            version: 1,
            asOfDate: '2026-06-30',
            entityName: 'Polaris Bio, Inc.',
            currency: 'USD',
            instruments: [
                {
                    id: 'common',
                    name: 'Common Stock',
                    kind: 'common',
                    sharesOutstanding: 8_000_000,
                    sharesAuthorized: 8_000_000,
                    issuePrice: 0.0001,
                    issueDate: '2020-01-01',
                    liquidationMultiple: null,
                    participation: null,
                    participationCap: null,
                    seniority: null,
                    conversionRatio: null,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'prefA',
                    name: 'Series A Preferred',
                    kind: 'preferred',
                    sharesOutstanding: 3_000_000,
                    sharesAuthorized: 3_000_000,
                    issuePrice: 3.0,
                    issueDate: '2022-04-01',
                    liquidationMultiple: 1.0,
                    participation: 'nonParticipating',
                    participationCap: null,
                    seniority: 1,
                    conversionRatio: 1.0,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
            ],
            convertibles: [],
        },
        assumptions: {
            volatility: 0.5,
            timeToLiquidity: 4.0,
            riskFreeRate: 0.0425,
            dividendYield: 0,
            method: 'pwer',
            methodRationale: 'PWERM justified by near-term liquidity events on the horizon (filed S-1).',
        },
        equityValue: {
            source: 'dcf',
            anchorInstrumentId: null,
            anchorPricePerShare: null,
            directEquityValue: 30_000_000,
            armLengthFlag: true,
            transactionDate: '2026-04-01',
        },
        discounts: { dlomModel: 'fixed', dlomPercentage: 0.2, minorityDiscount: 0 },
        compliance: {
            valuationDate: '2026-07-01',
            grantDate: null,
            safeHarbor: 'independentAppraisal',
        },
        solverTolerance: 0.005,
        solverMaxIterations: 100,
    },
};
// ── Dataset 5 — Hybrid branch ─────────────────────────────────────────────
const hybridBlend = {
    id: 'demo_hybrid',
    name: 'Hybrid OPM/PWERM Blend',
    description: 'Hybrid method: OPM backsolve blended with a PWERM view, for a company with both a recent financing round and credible scenario forecasts.',
    inputs: {
        valuationId: 'demo_hybrid',
        capTableSnapshot: {
            id: 'cap_demo_hybrid',
            version: 1,
            asOfDate: '2026-06-30',
            entityName: 'Meridian Cloud, Inc.',
            currency: 'USD',
            instruments: [
                {
                    id: 'common',
                    name: 'Common Stock',
                    kind: 'common',
                    sharesOutstanding: 12_000_000,
                    sharesAuthorized: 12_000_000,
                    issuePrice: 0.0001,
                    issueDate: '2019-01-01',
                    liquidationMultiple: null,
                    participation: null,
                    participationCap: null,
                    seniority: null,
                    conversionRatio: null,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'prefA',
                    name: 'Series B Preferred',
                    kind: 'preferred',
                    sharesOutstanding: 4_000_000,
                    sharesAuthorized: 4_000_000,
                    issuePrice: 3.5,
                    issueDate: '2025-01-15',
                    liquidationMultiple: 1.0,
                    participation: 'nonParticipating',
                    participationCap: null,
                    seniority: 1,
                    conversionRatio: 1.0,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
                {
                    id: 'pool',
                    name: 'Option Pool',
                    kind: 'pool',
                    sharesOutstanding: 0,
                    sharesAuthorized: 1_800_000,
                    issuePrice: 0.0001,
                    issueDate: null,
                    liquidationMultiple: null,
                    participation: null,
                    participationCap: null,
                    seniority: null,
                    conversionRatio: null,
                    strikePrice: null,
                    vestingMonths: null,
                    expirationDate: null,
                    antiDilution: null,
                    notes: '',
                },
            ],
            convertibles: [],
        },
        assumptions: {
            volatility: 0.5,
            timeToLiquidity: 4.5,
            riskFreeRate: 0.0425,
            dividendYield: 0,
            method: 'hybrid',
            methodRationale: 'Hybrid: OPM backsolve off the Series B plus a PWERM overlay for credible upside/downside scenarios.',
        },
        equityValue: {
            source: 'backsolve',
            anchorInstrumentId: 'prefA',
            anchorPricePerShare: 3.5,
            directEquityValue: null,
            armLengthFlag: true,
            transactionDate: '2026-03-20',
        },
        discounts: { dlomModel: 'fixed', dlomPercentage: 0.22, minorityDiscount: 0 },
        compliance: {
            valuationDate: '2026-07-01',
            grantDate: null,
            safeHarbor: 'independentAppraisal',
        },
        solverTolerance: 0.005,
        solverMaxIterations: 100,
    },
};
/**
 * Catalogue of all available demo datasets, in display order.
 * The Load Demo modal iterates this array; Settings → Clear Demo Data uses
 * `clearDemoData()` in the store to reset whatever the user has loaded.
 */
export const demoDatasets = [
    seedStageClean,
    seriesAWithPool,
    lateStageMultiClass,
    pwermScenario,
    hybridBlend,
];
//# sourceMappingURL=demoDatasets.js.map